import { useClientPortalAPI } from "@certn/api";
import { BasicButton, Loader, Stack, Text } from "@certn/cascadia-react";
import { useTranslation } from "react-i18next";

import { CopiableValue } from "./CopiableValue";

interface NamedItem {
  id: string;
  name: string;
}

interface ListSectionProps {
  title: string;
  testID: string;
  items?: NamedItem[];
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  emptyMessage: string;
  emptyAction?: { label: string; href: string };
}

function ListSection({
  title,
  testID,
  items,
  isLoading,
  error,
  onRetry,
  emptyMessage,
  emptyAction,
}: ListSectionProps) {
  const { t } = useTranslation();

  return (
    <Stack gap="space-3" testID={testID}>
      <Text style="global/heading-small">{title}</Text>
      {isLoading && !items ? (
        <Loader />
      ) : error ? (
        <Stack orientation="horizontal" gap="space-4" align="center">
          <Text style="form/body-secondary" testID={`${testID}-error`}>
            {t("Unable to load")}
          </Text>
          <BasicButton
            type="button"
            testID={`${testID}-retry`}
            onPress={onRetry}
          >
            {t("Retry")}
          </BasicButton>
        </Stack>
      ) : items && items.length > 0 ? (
        items.map(({ id, name }) => (
          <CopiableValue
            key={id}
            testID={`${testID}-item-${id}`}
            label={name}
            value={id}
          />
        ))
      ) : (
        <Stack gap="space-2">
          <Text style="form/body-secondary" testID={`${testID}-empty`}>
            {emptyMessage}
          </Text>
          {emptyAction && (
            <a href={emptyAction.href} target="_blank" rel="noreferrer">
              {emptyAction.label}
            </a>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export function SuccessFactorsPackagesAndTeamsList() {
  const { t } = useTranslation();
  const {
    data: packages,
    isLoading: isPackagesLoading,
    error: packagesError,
    mutate: retryPackages,
  } = useClientPortalAPI(
    "/api/internal/client-portal/ats-integrations/packages/"
  );
  const {
    data: customerGroups,
    isLoading: isCustomerGroupsLoading,
    error: customerGroupsError,
    mutate: retryCustomerGroups,
  } = useClientPortalAPI(
    "/api/internal/client-portal/ats-integrations/customer-groups/"
  );

  return (
    <Stack gap="space-5" testID="successfactors-packages-and-teams">
      <Stack gap="space-2">
        <Text style="global/heading-medium">{t("Packages and teams")}</Text>
        <Text style="form/body-primary">
          {t(
            "Copy these IDs into SuccessFactors. Use a package ID for packageId and a team ID for teamId."
          )}
        </Text>
      </Stack>
      <ListSection
        title={t("Packages")}
        testID="successfactors-packages-list"
        items={packages as NamedItem[] | undefined}
        isLoading={isPackagesLoading}
        error={packagesError}
        onRetry={() => {
          void retryPackages();
        }}
        emptyMessage={t("No ATS packages found.")}
        emptyAction={{
          label: t("Create a package"),
          href: "/packages/create/",
        }}
      />
      <ListSection
        title={t("Teams")}
        testID="successfactors-teams-list"
        items={customerGroups as NamedItem[] | undefined}
        isLoading={isCustomerGroupsLoading}
        error={customerGroupsError}
        onRetry={() => {
          void retryCustomerGroups();
        }}
        emptyMessage={t("No active teams found.")}
      />
    </Stack>
  );
}
